import type { ReactNode } from "react";
import { makeStyles, shorthands } from "@fluentui/react-components";
import { BRAND } from "../risikoanalyseStep/tokens";
import { sectionColorHex } from "../risikoanalyseStep/sectionColor";
import {
  CONTRACT_MASTER_SECTIONS,
  type ContractMasterSection,
} from "./masterSections";

/**
 * Stacked, all-sections-visible contract detail view. One unified shell for
 * every contract kind. Sections are rendered vertically as cards; the
 * `renderSection` callback returns the section content (returning null shows a
 * default placeholder).
 */
const useStyles = makeStyles({
  root: {
    paddingLeft: "24px",
    paddingRight: "24px",
    paddingBottom: "32px",
    maxWidth: "1400px",
    marginLeft: "auto",
    marginRight: "auto",
    display: "flex",
    flexDirection: "column",
    rowGap: "16px",
    fontFamily: BRAND.fontPrimary,
    color: BRAND.ink,
    minHeight: "100%",
  },
  stickyHeader: {
    position: "sticky",
    top: 0,
    zIndex: 10,
    marginLeft: "-24px",
    marginRight: "-24px",
    paddingTop: "16px",
    paddingBottom: "14px",
    paddingLeft: "24px",
    paddingRight: "24px",
    backgroundColor: "#f8f8f8",
    display: "flex",
    flexDirection: "column",
    rowGap: "10px",
    ...shorthands.borderBottom("1px", "solid", "rgba(45,20,43,0.08)"),
  },
  kindRow: {
    display: "flex",
    alignItems: "center",
    columnGap: "12px",
    flexWrap: "wrap",
  },
  kindIcon: {
    width: "36px",
    height: "36px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    color: BRAND.white,
    backgroundColor: BRAND.purple,
    ...shorthands.borderRadius("10px"),
  },
  kindLabel: {
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.78rem",
    fontWeight: 700,
    color: BRAND.muted,
    letterSpacing: "0.05em",
    textTransform: "uppercase",
  },
  kategoriePill: {
    display: "inline-block",
    paddingTop: "4px",
    paddingBottom: "4px",
    paddingLeft: "10px",
    paddingRight: "10px",
    fontSize: "0.72rem",
    fontWeight: 700,
    letterSpacing: "0.05em",
    textTransform: "uppercase",
    color: BRAND.white,
    backgroundColor: BRAND.ink,
    ...shorthands.borderRadius("999px"),
  },
  titleRow: {
    display: "flex",
    alignItems: "baseline",
    columnGap: "12px",
    flexWrap: "wrap",
  },
  title: {
    fontFamily: BRAND.fontAccent,
    fontSize: "clamp(1.4rem, 3.4vw, 2rem)",
    fontWeight: 400,
    color: BRAND.ink,
    margin: 0,
  },
  metaRow: {
    display: "flex",
    flexWrap: "wrap",
    columnGap: "24px",
    rowGap: "8px",
  },
  metaBlock: {
    display: "flex",
    flexDirection: "column",
    rowGap: "2px",
    minWidth: "180px",
  },
  metaLabel: {
    fontSize: "0.72rem",
    fontWeight: 700,
    color: BRAND.muted,
    letterSpacing: "0.05em",
    textTransform: "uppercase",
  },
  metaValue: {
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.95rem",
    color: BRAND.ink,
    fontWeight: 600,
  },
  commandBar: {
    display: "flex",
    flexWrap: "wrap",
    columnGap: "8px",
    rowGap: "8px",
    paddingTop: "4px",
  },
  sectionCard: {
    backgroundColor: BRAND.white,
    ...shorthands.border("1px", "solid", BRAND.borderDashed),
    ...shorthands.borderRadius("12px"),
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
  },
  sectionStripe: {
    height: "4px",
  },
  sectionHeader: {
    display: "flex",
    alignItems: "center",
    columnGap: "10px",
    flexWrap: "wrap",
    paddingTop: "14px",
    paddingBottom: "10px",
    paddingLeft: "20px",
    paddingRight: "20px",
  },
  sectionSpacer: {
    flexGrow: 1,
  },
  editBtn: {
    appearance: "none",
    cursor: "pointer",
    backgroundColor: BRAND.white,
    backgroundImage: "none",
    color: BRAND.ink,
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.82rem",
    fontWeight: 700,
    paddingTop: "6px",
    paddingBottom: "6px",
    paddingLeft: "12px",
    paddingRight: "12px",
    ...shorthands.border("1px", "solid", BRAND.border),
    ...shorthands.borderRadius("999px"),
    transitionProperty: "border-color, color",
    transitionDuration: "120ms",
    ":hover": {
      ...shorthands.borderColor(BRAND.purple),
      color: BRAND.purple,
    },
  },
  sectionPill: {
    display: "inline-block",
    paddingTop: "3px",
    paddingBottom: "3px",
    paddingLeft: "10px",
    paddingRight: "10px",
    fontSize: "0.72rem",
    fontWeight: 700,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    color: BRAND.white,
    ...shorthands.borderRadius("999px"),
  },
  sectionTitle: {
    fontFamily: BRAND.fontAccent,
    fontSize: "1.2rem",
    fontWeight: 500,
    color: BRAND.ink,
    margin: 0,
  },
  sectionBody: {
    paddingTop: "8px",
    paddingBottom: "20px",
    paddingLeft: "20px",
    paddingRight: "20px",
    display: "flex",
    flexDirection: "column",
    rowGap: "12px",
  },
  placeholder: {
    paddingTop: "12px",
    paddingBottom: "12px",
    color: BRAND.muted,
    fontStyle: "italic",
    fontSize: "0.92rem",
  },
});

export type ContractDetailHeader = {
  kindLabel: string;
  kindIcon: ReactNode;
  kategorie?: string;
  contractNbr?: string;
  contractIdFallback?: string | number;
  contactName?: string;
  vertragsartLabel?: string;
  produktLabel?: string;
};

type Props = {
  header: ContractDetailHeader;
  commandBar?: ReactNode;
  renderSection: (section: ContractMasterSection) => ReactNode;
  onEditSection?: (section: ContractMasterSection) => void;
};

const ContractDetailView = ({
  header,
  commandBar,
  renderSection,
  onEditSection,
}: Props) => {
  const styles = useStyles();
  const title = header.contractNbr
    ? `Vertrag ${header.contractNbr}`
    : header.contractIdFallback != null
      ? `Vertrag #${header.contractIdFallback}`
      : "Vertrag";

  return (
    <div className={styles.root}>
      <div className={styles.stickyHeader}>
        <div className={styles.kindRow}>
          <span className={styles.kindIcon}>{header.kindIcon}</span>
          <span className={styles.kindLabel}>{header.kindLabel}</span>
          {header.kategorie && (
            <span className={styles.kategoriePill}>{header.kategorie}</span>
          )}
        </div>
        <div className={styles.titleRow}>
          <h1 className={styles.title}>{title}</h1>
        </div>
        <div className={styles.metaRow}>
          <div className={styles.metaBlock}>
            <span className={styles.metaLabel}>Kontakt</span>
            <span className={styles.metaValue}>{header.contactName ?? "—"}</span>
          </div>
          {header.vertragsartLabel && (
            <div className={styles.metaBlock}>
              <span className={styles.metaLabel}>Vertragsart</span>
              <span className={styles.metaValue}>{header.vertragsartLabel}</span>
            </div>
          )}
          {header.produktLabel && (
            <div className={styles.metaBlock}>
              <span className={styles.metaLabel}>Produkt / Tarifierungsart</span>
              <span className={styles.metaValue}>{header.produktLabel}</span>
            </div>
          )}
        </div>
        {commandBar && <div className={styles.commandBar}>{commandBar}</div>}
      </div>

      {CONTRACT_MASTER_SECTIONS.map((section) => {
        const color = sectionColorHex[section.color];
        const content = renderSection(section);
        return (
          <section key={section.key} className={styles.sectionCard}>
            <div className={styles.sectionStripe} style={{ backgroundColor: color }} />
            <div className={styles.sectionHeader}>
              <span className={styles.sectionPill} style={{ backgroundColor: color }}>
                {section.shortTitle}
              </span>
              <h2 className={styles.sectionTitle}>{section.title}</h2>
              <span className={styles.sectionSpacer} />
              {onEditSection && (
                <button
                  type="button"
                  className={styles.editBtn}
                  onClick={() => onEditSection(section)}
                  aria-label={`${section.title} bearbeiten`}
                >
                  Bearbeiten
                </button>
              )}
            </div>
            <div className={styles.sectionBody}>
              {content ?? (
                <div className={styles.placeholder}>Daten folgen…</div>
              )}
            </div>
          </section>
        );
      })}
    </div>
  );
};

export default ContractDetailView;
